import path from 'path';
import { unityToThreejs, threejsToUnity } from '@/editor-adapter/engine';
import { getAssetFilesFromArtwork } from '@/services/PanelsServices';
import { TransformControlModeTypes, AssetTypes } from '@/services/constants';
/**
 * @typedef PanelsAsset
 * @property {String} assetID
 * @property {String} assetType
 * @property {String} assetPath
 */
/**
 * @typedef PanelsScene
 * @property {String} sceneID
 * @property {Number[]} position
 * @property {Number[]} rotation
 * @property {Number[]} scale
 */
/**
 * @typedef PanelsElement
 * @property {String} assetID
 * @property {String} sceneID
 * @property {String} parentID
 * @property {Vector3} position
 * @property {Euler} rotation
 * @property {Vector3} scale
 */

const state = {
  version: 1,
  settings: {
    movable: true,
  },
  /** @type {PanelsScene[]} */
  scenes: [
    {
      sceneID: 'root',
      position: [0, 0, 0],
      rotation: [0, 0, 0],
      scale: [1, 1, 1]
    }
  ],
  /** @type {PanelsElement[]} */
  panels: [],

  // Copies of properties for resetState action which defines a state from which the changes are built upon
  // I.e. stops the user from undoing some of the scene bootstraping

};

const getters = {
  /**
   * @returns {Array<PanelsScene>}
   */
  getSceneList(state) {
    return state.scenes;
  },
  /**
   * @param {String} sceneObjectId
   * @returns {Array<PanelsElement>}
   */
  getPanelListFromScene(state) {
    return sceneObjectId =>
      state.panels.filter(panel => panel.parentID === sceneObjectId);
  },
  /**
   * @param {PanelsElement} sceneID
   * @returns {(PanelsElement|null)}
   */
  getPanel(state) {
    return sceneID => state.panels.find(panel => panel.sceneID === sceneID);
  },

  /**
   * Returns true if scene has no content
   * @returns {Boolean}
   */
  isDefaultScene(state) {
    return state.panels.length === 0 && state.scenes.length === 1;
  },
  /**
   * Returns hierarchical scene graph for storage in graphql database (as an JS object)
   */
  sceneConfig(state, getters, rootState, rootGetters) {
    return artworkId => {
      // Get assets if artwork id is passed
      let assets = [];
      if (artworkId) {
        const artwork = rootGetters.artworkById(artworkId);
        const files = getAssetFilesFromArtwork(artwork);
        assets = files.map(file => ({
          assetID: file.fileID,
          assetType: file.type,
          assetPath: path.basename(file.path),
        }));
      }

      // Coordinate inversion (Three.js → Unity) is delegated to the shared
      // converter in @eyejack/editor. See also actions["PanelsScene/setSceneConfig"].
      return threejsToUnity({
        version: 1,
        settings: { movable: true },
        assets,
        scenes: state.scenes,
        elements: state.panels,
      });
    };
  }
};

const mutations = {
  // SET_PANELS_ASSETS(state, assets) {
  //   state.assets = assets;
  // },
  SET_PANELS_SCENES(state, scenes) {
    state.scenes = scenes;
  },
  SET_PANELS_ELEMENTS(state, panels) {
    state.panels = panels;
  },
  // Resets scene to empty
  clear(state) {
    console.warn('Clearing scene state');
    state.version = 1;
    state.scenes = [{
      sceneID: 'root',
      position: [0, 0, 0],
      rotation: [0, 0, 0],
      scale: [1, 1, 1]
    }];
    state.panels = [];
  }
};

const actions = {
  /**
   * @param {String} artworkId - Artwork Id to get assets from
   * @param {String} parentID - Scene object to add panel too
   * @param {String} sceneID - UID for new panels element
   * @param {String} assetID - Asset Id (asset.files.fileID) to get the right asset from the artwork
   */
  addPanelsElement(
    { commit, state, rootGetters },
    { artworkId, parentID, sceneID, assetID }
  ) {
    console.log(`Panels Editor: Adding panel: ${sceneID} to ${state.panels.length} panels.`);
    const artwork = rootGetters.artworkById(artworkId);
    if (artwork) {
      const asset =
        artwork.files.length > 0
          ? artwork.files.find(file => {
            return file.fileID === assetID;
          })
          : null;
      if (asset !== null) {
        // Always drop new elements at a fixed spawn point. (Previously this
        // stepped forward along Z based on the foremost existing panel, so the
        // start position drifted with each add / wherever the last asset moved.)
        const position = [0, 1, 0];

        let scale;
        if (asset.type === AssetTypes.GLB) {
          // 3D model: ship unit scale; the editor's ModelElement self-normalizes
          // the mesh's actual size. (GLB has no 2D width/height.)
          scale = [1, 1, 1];
        } else {
          // Get normalized dimensions (unit vector of width/height)
          const len = Math.hypot(asset.width, asset.height) || 1;
          const x = asset.width / len;
          const y = asset.height / len;
          // and scale it to [1, 0]
          const maxDimension = Math.max(x, y);
          let width = x * (1 / maxDimension);
          let height = y * (1 / maxDimension);
          // Round to 3 decimal place
          width = Math.round((width + Number.EPSILON) * 1000) / 1000;
          height = Math.round((height + Number.EPSILON) * 1000) / 1000;
          scale = [width, height, 1];
        }
        const newPanelsElement = {
          assetID,
          sceneID,
          parentID,
          position,
          rotation: [0, 0, 0],
          scale
        };
        commit('SET_PANELS_ELEMENTS', [...state.panels, newPanelsElement]);
        // Set transform controls to position mode
        commit('SET_TRANSFORM_CONTROLS_MODE', {
          transformControlsMode: TransformControlModeTypes.TRANSLATE,
        }, { root: true });
      } else {
        console.error(
          'Error. Cannot create new panel with assetID of ' +
            assetID +
            ' as asset was not found.'
        );
      }
    } else {
      console.error(
        'Error.  Cannot create new panel as artwork with ' +
          artworkId +
          ' does not exist.'
      );
    }
  },
  /**
   * Removes a panels element, also unselects it if it's selected
   *
   * @param {*} { commit, state, rootState, dispatch }
   * @param {*} { sceneID }
   */
  removePanelsElement({ commit, state, rootState, dispatch }, { sceneID }) {
    console.log(`Panels Editor: Deleting panel: ${sceneID} from ${state.panels.length} panels.`);
    const newPanels = state.panels.filter(panel => panel.sceneID !== sceneID);
    commit('SET_PANELS_ELEMENTS', newPanels);
    const { selectedElements } = rootState.PanelsEditor;
    if (selectedElements.includes(sceneID)) {
      dispatch('unselectAll', null, { root: true });
    }
  },
  /**
   * Removes all panels with an asset id.  Useful when deleting an asset
   * @param {*} {dispatch, state}
   * @param {String} {assetID}
   */
  removePanelsElementsByAssetId({ state, dispatch }, { assetID }) {
    const panelsToRemove = state.panels.filter(
      panel => panel.assetID === assetID
    );
    panelsToRemove.forEach(panel => {
      const { sceneID } = panel;
      dispatch('removePanelsElement', { sceneID });
    });
  },
  /**
   * Transforms a panels element (position, rotation, scale)
   * @param {Object} obj
   * @param {String} obj.sceneID - Id of the panel to transform
   * @param {Array<Number>} obj.position - New X,Y,Z position of panel
   * @param {Array<Number>} obj.rotation - New X,Y,Z rotation of panel (in degrees)
   * @param {Array<Number>} obj.scale - New X,Y,Z scale of panel
   */
  transformPanelsElement(
    { commit, state },
    { sceneID, position, rotation, scale }
  ) {
    // Apply modification
    const modifiedPanels = state.panels.map(panel => {
      if (panel.sceneID === sceneID) {
        return { ...panel, position, rotation, scale };
      } else {
        return panel;
      }
    });
    // Reset panels elements
    commit('SET_PANELS_ELEMENTS', modifiedPanels);
  },

  /**
   * sets the internal scene graph from a JSON.stringified sceneConfig (inside artwork.sceneConfig)
   */
  setSceneConfig({ commit }, config) {
    console.log(`Panels Editor: Loading ${config.scenes.length} scenes from scene config.`);

    // Coordinate inversion (Unity → Three.js) is delegated to the shared
    // converter in @eyejack/editor. See also getters["PanelsScene/sceneConfig"].
    const internal = unityToThreejs({
      version: config.version ?? 1,
      settings: config.settings ?? { movable: true },
      assets: config.assets ?? [],
      scenes: config.scenes,
    });

    console.debug(`Panels Editor: \tLoading ${internal.scenes.length} scenes.`, internal.scenes);
    commit('SET_PANELS_SCENES', internal.scenes);

    console.debug(`Panels Editor: \tLoading ${internal.elements.length} panels.`, internal.elements);
    commit('SET_PANELS_ELEMENTS', internal.elements);
  },
  /**
   * Applies a mutation to the PanelsScene that modifies nothing.
   * Needed for a workaround for a bug in the vuex-undo-redo plugin.
   */
  touchState({ commit, state }) {
    commit('SET_PANELS_ELEMENTS', state.panels);
  }
};

export default {
  state,
  getters,
  mutations,
  actions,
  namespaced: true
};
